import { useState } from "react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import {
  applyOrganisationImport,
  previewOrganisationImport,
  type OrganisationImportNode,
  type OrganisationImportPlan,
} from "../../api/organisationImport";
import { AdminGate } from "../../components/admin/AdminGate";
import { Modal } from "../../components/admin/Modal";
import styles from "../../components/admin/Admin.module.css";
import { useToast } from "../../components/toast/toastContext";
import dialogStyles from "./OrganisationImportDialog.module.css";

const ImportPermission = "organisation.import";
const CatalogueSourceUrl = "https://github.com/rws-language-tech/rws-backstage-catalog/blob/main/rws-group-org.yaml";

type GroupKey = "conflict" | "create" | "change" | "unchanged";

/**
 * Previews and applies a Backstage organisation catalogue (decided 13 October 2026), opened from the Groups
 * administration page. Nothing changes until the previewed plan is applied. A successful apply calls
 * `onApplied` so the caller can reload its catalogues, and closes the dialog; the toast reports the outcome.
 */
export function OrganisationImportDialog({ onClose, onApplied }: { onClose: () => void; onApplied: () => void }): React.JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const [yaml, setYaml] = useState("");
  const [plan, setPlan] = useState<OrganisationImportPlan | null>(null);
  const [busy, setBusy] = useState<"preview" | "apply" | null>(null);

  const preview = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy("preview");
    const result = await previewOrganisationImport(yaml);
    setBusy(null);
    if (result.kind === "ok") {
      setPlan(result.response);
    } else {
      setPlan(null);
      toast.show(problemMessage(result, t), "error");
    }
  };

  const apply = async () => {
    if (!plan?.fingerprint) {
      return;
    }

    setBusy("apply");
    const result = await applyOrganisationImport(yaml, plan.fingerprint);
    setBusy(null);
    if (result.kind === "ok") {
      toast.show(t("organisationImport.applied", { count: result.response.changesApplied }), "success");
      if (result.response.warnings.length > 0) {
        toast.show(t("organisationImport.warning", { message: result.response.warnings.join(" ") }), "info");
      }
      setPlan(null);
      onApplied();
      onClose();
    } else {
      setPlan(null);
      toast.show(problemMessage(result, t), "error");
    }
  };

  const groups = plan ? groupNodes(plan.nodes) : null;

  return (
    <Modal title={t("organisationImport.title")} onClose={onClose} wide>
      <AdminGate permissions={[ImportPermission]} title={t("admin.entitlement.denied.title")} description={t("admin.entitlement.denied.description")}>
        <p className={styles.note}>{t("organisationImport.description")}</p>
        <p className={styles.note}>
          <a href={CatalogueSourceUrl} target="_blank" rel="noreferrer">{t("organisationImport.sourceLink")}</a>
        </p>
        <form className={dialogStyles.form} onSubmit={(event) => void preview(event)}>
          <div className={dialogStyles.field}>
            <label htmlFor="organisation-import-yaml">{t("organisationImport.inputLabel")}</label>
            <textarea
              id="organisation-import-yaml"
              className={dialogStyles.textarea}
              value={yaml}
              rows={14}
              spellCheck={false}
              data-autofocus
              onChange={(event) => {
                setYaml(event.target.value);
                setPlan(null);
              }}
            />
          </div>
          <div className={dialogStyles.actions}>
            <button type="submit" className="button button-secondary" disabled={busy !== null || yaml.trim() === ""}>
              {t("organisationImport.preview")}
            </button>
            <button type="button" className="button button-primary" disabled={busy !== null || !plan?.canApply || plan.changeCount === 0} onClick={() => void apply()}>
              {t("organisationImport.apply")}
            </button>
          </div>
        </form>

        {plan === null && <p className={styles.note}>{t("organisationImport.empty")}</p>}

        {plan !== null && groups !== null && (
          <div className={dialogStyles.results}>
            <p className={styles.note} role="status">
              {t("organisationImport.summary", { changes: plan.changeCount, conflicts: groups.conflict.length, unchanged: groups.unchanged.length })}
            </p>
            {!plan.canApply && <p className={styles.note}>{t("organisationImport.resolveConflicts")}</p>}

            {(["conflict", "create", "change", "unchanged"] as GroupKey[]).map((key) =>
              groups[key].length === 0 ? null : (
                <section key={key} className={dialogStyles.group} aria-labelledby={`organisation-import-group-${key}`}>
                  <h3 id={`organisation-import-group-${key}`}>{t(`organisationImport.groups.${key}`, { count: groups[key].length })}</h3>
                  <table className={dialogStyles.diffTable}>
                    <thead>
                      <tr>
                        <th scope="col">{t("organisationImport.columns.unit")}</th>
                        <th scope="col">{t("organisationImport.columns.kind")}</th>
                        <th scope="col">{t("organisationImport.columns.parent")}</th>
                        <th scope="col">{t("organisationImport.columns.manager")}</th>
                        <th scope="col">{t("organisationImport.columns.action")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groups[key].map((node) => (
                        <tr key={node.backstageName}>
                          <th scope="row">
                            {node.title}
                            <span className={dialogStyles.muted}> ({node.backstageName})</span>
                          </th>
                          <td>{t(`organisationImport.kinds.${node.kind.toLowerCase()}`)}</td>
                          <td>{node.parentName}</td>
                          <td>{node.managerEmail ?? t("organisationImport.noManager")}</td>
                          <td>{actionText(node, t)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </section>
              ),
            )}

            {plan.managersToCreate.length > 0 && (
              <section className={dialogStyles.group} aria-labelledby="organisation-import-managers">
                <h3 id="organisation-import-managers">{t("organisationImport.managersToCreate")}</h3>
                <ul>
                  {plan.managersToCreate.map((email) => <li key={email}>{email}</li>)}
                </ul>
              </section>
            )}

            {plan.notInSource.length > 0 && (
              <section className={dialogStyles.group} aria-labelledby="organisation-import-not-in-source">
                <h3 id="organisation-import-not-in-source">{t("organisationImport.notInSource")}</h3>
                <ul>
                  {plan.notInSource.map((row) => <li key={row.id}>{row.title} <span className={dialogStyles.muted}>({row.backstageName})</span></li>)}
                </ul>
              </section>
            )}
          </div>
        )}
      </AdminGate>
    </Modal>
  );
}

function groupNodes(nodes: OrganisationImportNode[]): Record<GroupKey, OrganisationImportNode[]> {
  const groups: Record<GroupKey, OrganisationImportNode[]> = { conflict: [], create: [], change: [], unchanged: [] };
  for (const node of nodes) {
    if (node.status === "Conflict") {
      groups.conflict.push(node);
    } else if (node.status === "Unchanged") {
      groups.unchanged.push(node);
    } else if (node.changes.some((change) => change.kind === "Create")) {
      groups.create.push(node);
    } else {
      groups.change.push(node);
    }
  }

  return groups;
}

function actionText(node: OrganisationImportNode, t: TFunction): string {
  if (node.status === "Conflict") {
    return node.conflictReason ?? t("organisationImport.conflictUnknown");
  }

  if (node.status === "Unchanged") {
    return t("organisationImport.noChange");
  }

  return node.changes.map((change) => `${t(`organisationImport.changes.${change.kind}`)}: ${change.detail}`).join("; ");
}

function problemMessage(
  result: { kind: "denied" } | { kind: "invalid"; errors: string[] } | { kind: "conflict"; message: string; errors: string[] } | { kind: "error"; message: string },
  t: TFunction,
): string {
  switch (result.kind) {
    case "denied":
      return t("admin.entitlement.denied.description");
    case "invalid":
      return t("organisationImport.invalid", { count: result.errors.length, errors: result.errors.join(" ") });
    case "conflict":
      return result.errors.length > 0 ? `${result.message} ${result.errors.join(" ")}` : result.message;
    case "error":
      return result.message;
  }
}
